import {
  ApiErrorSchema,
  CancelReturnResponseSchema,
  CreateReturnResponseSchema,
  EvidenceUploadResponseSchema,
  LookupResponseSchema,
  ShopperCodeResponseSchema,
  ShopperOrdersResponseSchema,
  ShopperVerifyResponseSchema,
  StorefrontResponseSchema,
  TrackResponseSchema,
  type CancelReturnRequest,
  type CancelReturnResponse,
  type CreateReturnRequest,
  type CreateReturnResponse,
  type EvidenceUploadResponse,
  type LookupRequest,
  type LookupResponse,
  type ShopperCodeRequest,
  type ShopperCodeResponse,
  type ShopperOrdersRequest,
  type ShopperOrdersResponse,
  type ShopperVerifyRequest,
  type ShopperVerifyResponse,
  type StorefrontResponse,
  type TrackRequest,
  type TrackResponse,
} from "@sirdab/returns-shared";

import type { ApiResult } from "./result";

/**
 * `POST .../resolve-link`: the track token minted with a return, resolved into that
 * return's tracking view. The answer is the same body `/track` gives.
 *
 * A bad, expired or revoked token produces the SAME 404 as a wrong reference: the endpoint
 * does not say which, and this client must not infer it.
 */
export interface ResolveLinkRequest {
  /** Part of the address, so a link minted for one store cannot open through another. */
  merchantSlug: string;
  token: string;
}

/**
 * Typed client for every public endpoint in docs/06.
 *
 * Two rules hold everywhere in this file:
 *   1. Every response body — success AND error — is parsed through the shared Zod schema,
 *      so contract drift surfaces here as a `contract` failure instead of as `undefined`
 *      six components deep.
 *   2. Expected business failures are returned, never thrown. See `./result.ts`.
 *
 * Only the public (shopper) surface lives here. Merchant and internal endpoints are
 * service-to-service and have no business being reachable from a shopper's browser.
 */

const DEV_BASE_URL = "http://localhost:3000";

/**
 * Every shopper route is scoped to one merchant, so the store is part of the address
 * rather than a field in the body the server has to trust. The body still carries
 * `merchantSlug` (the schemas are shared) and the server ignores it in favour of the path.
 */
const SHOPPER_ROOT = "/api/v1/returns/shopper/merchants";

function merchantPath(slug: string, suffix = ""): string {
  return `${SHOPPER_ROOT}/${encodeURIComponent(slug)}${suffix}`;
}

/** Pure so it is testable without a Vite build. Never hardcodes a production host. */
export function resolveBaseUrl(env?: {
  VITE_API_BASE_URL?: string | undefined;
}): string {
  const configured = env?.VITE_API_BASE_URL?.trim();
  const base = configured && configured.length > 0 ? configured : DEV_BASE_URL;
  return base.replace(/\/+$/, "");
}

export type FetchLike = (
  input: string,
  init?: RequestInit,
) => Promise<Response>;

export interface ApiClientOptions {
  baseUrl?: string | undefined;
  fetch?: FetchLike | undefined;
  /** Abort budget. A hung request is indistinguishable from being offline to a shopper. */
  timeoutMs?: number | undefined;
}

export interface ApiClient {
  readonly baseUrl: string;
  /**
   * With a `shopperToken` from `verifyShopperCode` the phone may be left out: the server
   * uses the one the code proved. The token travels as a bearer header, never in the body
   * or the URL.
   */
  lookup: (
    body: LookupRequest,
    shopperToken?: string,
  ) => Promise<ApiResult<LookupResponse>>;
  requestShopperCode: (
    body: ShopperCodeRequest,
  ) => Promise<ApiResult<ShopperCodeResponse>>;
  verifyShopperCode: (
    body: ShopperVerifyRequest,
  ) => Promise<ApiResult<ShopperVerifyResponse>>;
  listShopperOrders: (
    body: ShopperOrdersRequest,
    shopperToken: string,
  ) => Promise<ApiResult<ShopperOrdersResponse>>;
  createReturn: (
    body: CreateReturnRequest,
    shopperToken?: string,
  ) => Promise<ApiResult<CreateReturnResponse>>;
  /**
   * One multipart POST of the photo itself, answered with the `uploadId` the submit
   * payload carries in `evidence[]`. The shopper's session authorises it, so the token is
   * required rather than optional.
   */
  uploadEvidence: (
    body: { merchantSlug: string; file: Blob },
    shopperToken: string,
  ) => Promise<ApiResult<EvidenceUploadResponse>>;
  resolveLink: (body: ResolveLinkRequest) => Promise<ApiResult<TrackResponse>>;
  /** The store itself: does it exist, is it taking returns, and how does it look. */
  getStorefront: (
    merchantSlug: string,
  ) => Promise<ApiResult<StorefrontResponse>>;
  track: (body: TrackRequest) => Promise<ApiResult<TrackResponse>>;
  cancelReturn: (
    reference: string,
    body: CancelReturnRequest,
  ) => Promise<ApiResult<CancelReturnResponse>>;
}

/** Minimal structural shape of a Zod schema — avoids importing zod into the portal. */
interface ParserLike<T> {
  safeParse: (value: unknown) =>
    | { success: true; data: T }
    | {
        success: false;
        error: { issues: readonly { path: PropertyKey[]; message: string }[] };
      };
}

function describeIssues(
  issues: readonly { path: PropertyKey[]; message: string }[],
): string[] {
  return issues.map(
    (issue) => `${issue.path.join(".") || "(root)"}: ${issue.message}`,
  );
}

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const baseUrl = options.baseUrl ?? resolveBaseUrl(readViteEnv());
  const doFetch: FetchLike =
    options.fetch ?? ((input, init) => globalThis.fetch(input, init));
  const timeoutMs = options.timeoutMs ?? 15_000;

  async function request<T>(
    path: string,
    body: unknown,
    schema: ParserLike<T>,
    bearer?: string,
  ): Promise<ApiResult<T>> {
    let response: Response;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      response = await doFetch(`${baseUrl}${path}`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          accept: "application/json",
          ...(bearer === undefined
            ? {}
            : { authorization: `Bearer ${bearer}` }),
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
    } catch (cause) {
      // Offline, DNS, CORS, abort. Nothing is known about the server's intent, so we must
      // not attribute a cause the shopper would read as a policy decision.
      return { ok: false, kind: "network", cause };
    } finally {
      clearTimeout(timer);
    }

    return parseResponse(response, schema);
  }

  /**
   * Sibling of `request` for the one multipart endpoint. It is NOT `request` with a
   * different body: that helper sets a JSON content-type (the boundary must be left to
   * `fetch`) and holds everyone to the same 15 second budget, which an 8MiB photo on a
   * mobile connection will blow through long before the server has had a chance to answer.
   * Hence the separate, longer abort budget below.
   */
  const UPLOAD_TIMEOUT_MS = 90_000;

  async function postMultipart<T>(
    path: string,
    form: FormData,
    schema: ParserLike<T>,
    bearer: string,
  ): Promise<ApiResult<T>> {
    let response: Response;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
    try {
      response = await doFetch(`${baseUrl}${path}`, {
        method: "POST",
        headers: {
          accept: "application/json",
          authorization: `Bearer ${bearer}`,
        },
        body: form,
        signal: controller.signal,
      });
    } catch (cause) {
      return { ok: false, kind: "network", cause };
    } finally {
      clearTimeout(timer);
    }

    return parseResponse(response, schema);
  }

  /**
   * The one GET the portal makes: the store's own profile, read before the shopper is
   * asked for anything (SAI-1384). Same failure vocabulary as `request` so a caller
   * branches on one shape.
   */
  async function get<T>(
    path: string,
    schema: ParserLike<T>,
  ): Promise<ApiResult<T>> {
    let response: Response;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      response = await doFetch(`${baseUrl}${path}`, {
        method: "GET",
        headers: { accept: "application/json" },
        signal: controller.signal,
      });
    } catch (cause) {
      return { ok: false, kind: "network", cause };
    } finally {
      clearTimeout(timer);
    }
    return parseResponse(response, schema);
  }

  async function parseResponse<T>(
    response: Response,
    schema: ParserLike<T>,
  ): Promise<ApiResult<T>> {
    let payload: unknown;
    try {
      payload = await response.json();
    } catch (cause) {
      if (response.ok) {
        return {
          ok: false,
          kind: "contract",
          status: response.status,
          issues: ["body is not JSON"],
        };
      }
      return { ok: false, kind: "network", cause };
    }

    if (!response.ok) {
      const parsedError = ApiErrorSchema.safeParse(payload);
      if (!parsedError.success) {
        // An error we cannot read is still an error, but it is a CONTRACT error — we will
        // not guess a code and we will not invent a reason.
        return {
          ok: false,
          kind: "contract",
          status: response.status,
          issues: describeIssues(parsedError.error.issues),
        };
      }
      return {
        ok: false,
        kind: "response",
        status: response.status,
        error: parsedError.data.error,
        reasonText: parsedError.data.reasonText ?? null,
        violations: parsedError.data.violations ?? [],
      };
    }

    const parsed = schema.safeParse(payload);
    if (!parsed.success) {
      return {
        ok: false,
        kind: "contract",
        status: response.status,
        issues: describeIssues(parsed.error.issues),
      };
    }
    return { ok: true, data: parsed.data };
  }

  return {
    baseUrl,

    lookup: (body, shopperToken) =>
      request(
        merchantPath(body.merchantSlug, "/lookup"),
        body,
        LookupResponseSchema,
        shopperToken,
      ),

    requestShopperCode: (body) =>
      request(
        merchantPath(body.merchantSlug, "/code"),
        body,
        ShopperCodeResponseSchema,
      ),

    verifyShopperCode: (body) =>
      request(
        merchantPath(body.merchantSlug, "/verify"),
        body,
        ShopperVerifyResponseSchema,
      ),

    listShopperOrders: (body, shopperToken) =>
      request(
        merchantPath(body.merchantSlug, "/orders"),
        body,
        ShopperOrdersResponseSchema,
        shopperToken,
      ),

    resolveLink: (body) =>
      request(
        merchantPath(body.merchantSlug, "/resolve-link"),
        body,
        TrackResponseSchema,
      ),

    createReturn: (body, shopperToken) =>
      request(
        merchantPath(body.merchantSlug, "/returns"),
        body,
        CreateReturnResponseSchema,
        shopperToken,
      ),

    uploadEvidence: (body, shopperToken) => {
      const form = new FormData();
      form.append("photo", body.file);
      return postMultipart(
        merchantPath(body.merchantSlug, "/uploads"),
        form,
        EvidenceUploadResponseSchema,
        shopperToken,
      );
    },

    getStorefront: (merchantSlug) =>
      get(merchantPath(merchantSlug), StorefrontResponseSchema),
    track: (body) =>
      request(
        merchantPath(body.merchantSlug, "/track"),
        body,
        TrackResponseSchema,
      ),

    /**
     * The reference travels in the BODY, like `/track`. It is half the shopper's
     * credential, and a path parameter is written into access logs, proxy logs, referrers
     * and error breadcrumbs — places nobody treats as holding secrets. The path form
     * (`/returns/:reference/cancel`) still exists server-side but is deprecated (docs/06)
     * and this client no longer uses it.
     */
    cancelReturn: (reference, body) =>
      request(
        merchantPath(body.merchantSlug, "/cancel"),
        { ...body, reference },
        CancelReturnResponseSchema,
      ),
  };
}

function readViteEnv(): { VITE_API_BASE_URL?: string | undefined } {
  try {
    return import.meta.env as unknown as {
      VITE_API_BASE_URL?: string | undefined;
    };
  } catch {
    return {};
  }
}

/** The app-wide instance. Screens use `useApi()`; tests build their own with a stub fetch. */
export const apiClient: ApiClient = createApiClient();
